import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { useOrderMarkets, useOrders } from "@web-app/api";
import { Avatar, Icon } from "@web-app/components";
import { useBrowserCache } from "@web-app/hooks";
import { INITIAL_APP_FILTER_DATA } from "@web-app/pages/common/Orders/data";
import OrderEditPanelWrapper from "@web-app/pages/common/OrdersSidebar/OrderEditPanelWrapper";
import { OptimizationItem, Order } from "@web-app/types";

import {
  itemMissingFieldsByProvider,
  unquotableItems,
} from "../providerIssues";
import { externalProviderDisplay } from "../utils";

type Props = {
  onOrderEdited: () => void;
  previewItems: OptimizationItem[];
};

export default function ResolveOrderIssuesStep({
  onOrderEdited,
  previewItems,
}: Props) {
  const [cpdFilter] = useBrowserCache("cpdAppFilter", INITIAL_APP_FILTER_DATA);
  const { data: marketsMap } = useOrderMarkets({
    countryCode: cpdFilter.countryCode,
  });
  const ordersFilter = useMemo(() => {
    if (cpdFilter.stateIds && cpdFilter.stateIds.length > 0) return cpdFilter;
    const allStateIds = Array.from(marketsMap?.values() || []).map(
      (state) => state.id,
    );
    return { ...cpdFilter, stateIds: [...allStateIds, "null"] };
  }, [cpdFilter, marketsMap]);
  const { data: ordersData } = useOrders(ordersFilter);

  const orderById = useMemo(() => {
    const map = new Map<string, Order>();
    Array.from(ordersData?.orders.values() || []).forEach((order) =>
      map.set(order.id, order),
    );
    return map;
  }, [ordersData]);

  const queryClient = useQueryClient();
  const pendingMutations = useIsMutating();
  const [refreshAfterEdit, setRefreshAfterEdit] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [openGaps, setOpenGaps] = useState<Set<string>>(new Set());

  // The panel closes without waiting for its save, so re-quoting right away
  // would read the order back exactly as it was before the edit.
  useEffect(() => {
    if (!refreshAfterEdit || pendingMutations > 0) return;
    setRefreshAfterEdit(false);
    // The panel only writes back to the dispatch page's cache entry, so this
    // step would otherwise keep handing it the pre-edit order.
    queryClient.invalidateQueries({ queryKey: ["orders"] });
    onOrderEdited();
  }, [refreshAfterEdit, pendingMutations, queryClient, onOrderEdited]);

  const blockedItems = unquotableItems(previewItems);

  const toggleGap = (gapKey: string) => {
    setOpenGaps((open) => {
      const next = new Set(open);
      if (!next.delete(gapKey)) next.add(gapKey);
      return next;
    });
  };

  const closeEditor = () => {
    setEditingOrder(null);
    setRefreshAfterEdit(true);
  };

  const missingCount = blockedItems.length;

  const editor = editingOrder && (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30">
      <div className="flex h-full w-[560px] flex-col overflow-y-auto bg-white shadow-xl">
        <OrderEditPanelWrapper order={editingOrder} onClose={closeEditor} />
      </div>
    </div>
  );

  if (missingCount === 0) {
    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center">
          <Icon name="circleCheck" className="mx-auto h-6 w-6 text-green-500" />
          <p className="mt-3 text-sm font-medium text-gray-900">
            Every selected order can be quoted
          </p>
          <p className="mt-1 text-sm text-gray-500">
            Proceed to compare what each provider charges.
          </p>
        </div>
        {editor}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
        <Icon
          name="fasCircleExclamation"
          className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500"
        />
        <div className="space-y-1">
          <p>
            {missingCount} selected{" "}
            {missingCount === 1 ? "order is" : "orders are"} missing additional
            information required by external providers for cost optimization.
          </p>
          <p>
            Each order below lists what every provider is missing separately, so
            use <strong>Edit order</strong> to fix them. You can also proceed as
            is, and a provider will simply not quote the{" "}
            {missingCount === 1 ? "order" : "orders"} it flagged.
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white">
        <ul className="divide-y divide-gray-100">
          {blockedItems.map((item) => {
            const order = orderById.get(item.itemId);
            const gaps = itemMissingFieldsByProvider(item);
            const name = order?.externalId || order?.displayId || item.itemId;
            const editable = item.kind === "ORDER" && !!order;
            return (
              <li
                key={item.itemId}
                className="flex items-center justify-between gap-4 px-6 py-3"
              >
                <div className="min-w-0 space-y-1">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {name}
                  </p>
                  {gaps.map((gap) => {
                    const gapKey = `${item.itemId}:${gap.kind}`;
                    const isOpen = openGaps.has(gapKey);
                    const provider = externalProviderDisplay(gap.kind);
                    return (
                      <div key={gap.kind} className="text-xs text-amber-600">
                        <button
                          type="button"
                          onClick={() => toggleGap(gapKey)}
                          aria-expanded={isOpen}
                          className="flex w-full items-start gap-1.5 text-left"
                        >
                          <span className="flex-shrink-0 leading-none">
                            <Avatar
                              name={provider.name}
                              image={provider.avatarUrl}
                              size={4}
                            />
                          </span>
                          <span className="min-w-0 flex-1 truncate">
                            <span className="font-semibold text-amber-900">
                              {provider.name}:
                            </span>{" "}
                            {isOpen
                              ? `${gap.issues.length} ${gap.issues.length === 1 ? "fix" : "fixes"} needed`
                              : gap.issues
                                  .map((issue) => issue.label)
                                  .join(", ")}
                          </span>
                          <Icon
                            name={isOpen ? "chevronDown" : "chevronRight"}
                            className="mt-0.5 h-3 w-3 flex-shrink-0"
                          />
                        </button>
                        {isOpen && (
                          <ul className="ml-5 mt-1 list-disc space-y-1 pl-3 marker:text-amber-400">
                            {gap.issues.map((issue) => (
                              <li key={`${issue.label}|${issue.detail}`}>
                                <span className="font-medium text-amber-800">
                                  {issue.label}
                                </span>
                                {issue.detail && (
                                  <span className="block font-normal text-amber-600">
                                    {issue.detail}
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
                {editable ? (
                  <button
                    type="button"
                    onClick={() => setEditingOrder(order)}
                    className="flex-shrink-0 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Edit order
                  </button>
                ) : (
                  <span className="flex-shrink-0 text-xs text-gray-400">
                    Edit the underlying orders
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      {editor}
    </div>
  );
}
